import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { useCurrentOrganization } from "../../../hooks/useCurrentOrganization";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import type { RouterContextWithSession } from "../../../router";
import {
  type TodoRow,
  useCreateTodo,
  useDeleteTodo,
  useTodos,
  useUpdateTodo,
} from "../../../hooks/useTodos";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "../../../components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";

export const Route = createFileRoute("/_admin/todos/")({
  // Phase 4: beforeLoad returns session for the loader to consume.
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  pendingComponent: () => <PageSkeleton rows={6} variant="list" />,
  errorComponent: createRouteError("Todos"),
  component: TodosPage,
});

// ── Status & Priority helpers ─────────────────────────────────────────────

const STATUS_LABELS: Record<TodoRow["status"], string> = {
  todo: "To Do",
  in_progress: "In Progress",
  completed: "Completed",
};

const STATUS_COLORS: Record<TodoRow["status"], string> = {
  todo: "bg-muted text-muted-foreground",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  completed:
    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
};

const PRIORITY_COLORS: Record<TodoRow["priority"], string> = {
  low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  medium:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  high: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

// ── Main Page ─────────────────────────────────────────────────────────────

function TodosPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return (
    <AuthenticatedTodos
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

// ── Authenticated sub-component (avoids conditional hook calls) ───────────

function AuthenticatedTodos({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const { currentOrganization, isLoading: isOrgLoading } = useCurrentOrganization();
  const orgId = currentOrganization?.id;
  const auth = { userId, accessToken, orgId };

  const [statusFilter, setStatusFilter] = useState<
    TodoRow["status"] | undefined
  >(undefined);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState<TodoRow["priority"]>("medium");

  const { data: todos, isLoading, error } = useTodos({
    ...auth,
    orgId,
    status: statusFilter,
  });
  const createMutation = useCreateTodo(auth);
  const updateMutation = useUpdateTodo(auth);
  const deleteMutation = useDeleteTodo(auth);

  if (isOrgLoading || !orgId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading organization…</p>
      </div>
    );
  }

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    try {
      await createMutation.mutateAsync({ title: newTitle.trim(), priority: newPriority });
      toast.success("Todo created");
      setNewTitle("");
      setNewPriority("medium");
      setShowAddForm(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create todo");
    }
  };

  // ── Status cycle (click to advance) ────────────────────────────────
  const cycleStatus = (todo: TodoRow) => {
    const next: Record<TodoRow["status"], TodoRow["status"]> = {
      todo: "in_progress",
      in_progress: "completed",
      completed: "todo",
    };
    updateMutation.mutate({ id: todo.id, updates: { status: next[todo.status] } }, { onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to update todo") });
  };

  return (
    <div className="">
      {/* Header */}
      <main className="container mx-auto p-6 space-y-6 max-w-3xl">
        {/* Add form */}
        {showAddForm && (
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-card-foreground">
              New Todo
            </h2>
            <Input
              type="text"
              placeholder="What needs to be done?"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              autoFocus
            />
            <div className="flex items-center gap-3">
              <label className="text-xs text-muted-foreground">Priority:</label>
              {(["low", "medium", "high"] as const).map((p) => (
                <Button
                  key={p}
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewPriority(p)}
                  className={`px-2.5 py-1 text-xs rounded-full capitalize ${
                    newPriority === p
                      ? PRIORITY_COLORS[p] + " ring-2 ring-offset-1 ring-primary"
                      : PRIORITY_COLORS[p]
                  }`}
                >
                  {p}
                </Button>
              ))}
              <Button
                onClick={handleAdd}
                disabled={!newTitle.trim()}
                loading={createMutation.isPending}
                className="ml-auto"
              >
                Add
              </Button>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Filter:</span>
          {([undefined, "todo", "in_progress", "completed"] as const).map(
            (s) => (
              <Button
                key={String(s)}
                variant={statusFilter === s ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(s)}
                className="rounded-full"
              >
                {s === undefined ? "All" : STATUS_LABELS[s]}
              </Button>
            )
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            {error.message}
          </div>
        )}

        {/* Empty state */}
        {todos?.length === 0 && !isLoading && (
          <div className="text-center py-16 space-y-4">
            <div className="text-4xl">✓</div>
            <p className="text-xl font-semibold text-foreground">No todos yet</p>
            <p className="text-muted-foreground">
              Create your first todo to get started tracking your work.
            </p>
            <Button onClick={() => setShowAddForm(true)}>+ Add Todo</Button>
          </div>
        )}

        {/* Todo list */}
        {!isLoading && todos && todos.length > 0 && (
          <ul className="space-y-2">
            {todos.map((todo) =>
              editingId === todo.id ? (
                <EditTodoRow
                  key={todo.id}
                  todo={todo}
                  onSave={(updates) => {
                    updateMutation.mutate({ id: todo.id, updates });
                    setEditingId(null);
                  }}
                  onCancel={() => setEditingId(null)}
                  isPending={updateMutation.isPending}
                />
              ) : (
                <TodoListItem
                  key={todo.id}
                  todo={todo}
                  onCycleStatus={() => cycleStatus(todo)}
                  onEdit={() => setEditingId(todo.id)}
                  onDelete={() =>
                    deleteMutation.mutate(todo.id, {
                      onSuccess: () => toast.success("Todo deleted"),
                      onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to delete todo"),
                    })
                  }
                  isDeleting={
                    deleteMutation.isPending &&
                    deleteMutation.variables === todo.id
                  }
                />
              )
            )}
          </ul>
        )}

        {/* Count */}
        {todos && todos.length > 0 && (
          <p className="text-xs text-center text-muted-foreground">
            {todos.filter((t) => t.status !== "completed").length} remaining ·{" "}
            {todos.filter((t) => t.status === "completed").length} completed
          </p>
        )}
      </main>
    </div>
  );
}

// ── Todo List Item ────────────────────────────────────────────────────────

function TodoListItem({
  todo,
  onCycleStatus,
  onEdit,
  onDelete,
  isDeleting,
}: {
  todo: TodoRow;
  onCycleStatus: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg border bg-card p-3 group hover:shadow-sm transition-shadow">
      {/* Status toggle (click to cycle) */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onCycleStatus}
        title={`Status: ${STATUS_LABELS[todo.status]} — click to advance`}
        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 p-0 ${
          todo.status === "completed"
            ? "bg-green-500 border-green-500 text-white hover:bg-green-600"
            : todo.status === "in_progress"
              ? "border-blue-500 bg-blue-100 dark:bg-blue-900 hover:bg-blue-200"
              : "border-muted-foreground hover:bg-muted"
        }`}
      >
        {todo.status === "completed" && (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </Button>

      {/* Title */}
      <span
        className={`flex-1 text-sm ${todo.status === "completed" ? "line-through text-muted-foreground" : "text-foreground"}`}
      >
        {todo.title}
      </span>

      {/* Badges */}
      <span
        className={`hidden sm:inline-flex px-2 py-0.5 text-xs rounded-full ${PRIORITY_COLORS[todo.priority]}`}
      >
        {todo.priority}
      </span>
      <span
        className={`hidden sm:inline-flex px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[todo.status]}`}
      >
        {STATUS_LABELS[todo.status]}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          title="Edit"
        >
          ✏️
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" title="Delete">
              ×
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Todo</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. The todo will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={onDelete}
              >
                Delete Todo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}

// ── Inline Edit Row ───────────────────────────────────────────────────────

function EditTodoRow({
  todo,
  onSave,
  onCancel,
  isPending,
}: {
  todo: TodoRow;
  onSave: (updates: {
    title?: string;
    status?: TodoRow["status"];
    priority?: TodoRow["priority"];
  }) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [title, setTitle] = useState(todo.title);
  const [status, setStatus] = useState(todo.status);
  const [priority, setPriority] = useState(todo.priority);

  return (
    <li className="rounded-lg border-2 border-primary bg-card p-3 space-y-2">
      <Input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
      />
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Status:</span>
          {(["todo", "in_progress", "completed"] as const).map((s) => (
            <Button
              key={s}
              variant="ghost"
              size="sm"
              onClick={() => setStatus(s)}
              className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[s]} ${status === s ? "ring-2 ring-offset-1 ring-primary" : ""}`}
            >
              {STATUS_LABELS[s]}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Priority:</span>
          {(["low", "medium", "high"] as const).map((p) => (
            <Button
              key={p}
              variant="ghost"
              size="sm"
              onClick={() => setPriority(p)}
              className={`px-2 py-0.5 text-xs rounded-full capitalize ${PRIORITY_COLORS[p]} ${priority === p ? "ring-2 ring-offset-1 ring-primary" : ""}`}
            >
              {p}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="ghost"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ title, status, priority })}
            disabled={!title.trim()}
            loading={isPending}
          >
            Save
          </Button>
        </div>
      </div>
    </li>
  );
}
